/**
 * TaskInteractionPanel - Unified component for task data collection.
 *
 * Used by both:
 * - Owner view (StepWorkspacePane) - plan owner completing their own tasks
 * - Delegate view (RequestThreadPage) - external user completing delegated tasks
 *
 * Provides consistent form + chat experience for structured data collection.
 */

import React, { useState, useCallback } from 'react';
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography, Alert } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChatIcon from '@mui/icons-material/Chat';
import { TaskHeader, type TaskStatus } from './TaskHeader';
import { TaskProgress, type FieldStatus } from './TaskProgress';
import { TaskChat, type ChatMessage } from './TaskChat';
import { TaskActions } from './TaskActions';
import { DynamicRequestForm } from '../request-thread';
import { determineUIMode, type UIMode } from '../../utils/request-thread-ui';
import { colors } from '../../theme/colors';

export interface AllowedField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  description?: string;
  enumValues?: string[];
}

export interface SatisfactionState {
  isReady: boolean;
  confidence: number;
  extractedFields: Record<string, unknown>;
  missingFields: string[];
  progressSummary: string;
}

export interface RequestSpec {
  ask: string;
  description?: string;
  allowedFields: AllowedField[];
  uiMode?: 'form' | 'chat' | 'auto';
}

export interface TaskInteractionPanelProps {
  // Content
  title: string;
  description?: string;
  requestSpec?: RequestSpec | null;

  // State
  status?: TaskStatus;
  values: Record<string, unknown>;
  satisfaction?: SatisfactionState | null;
  messages?: ChatMessage[];
  error?: string | null;

  // Mode control
  mode: 'owner' | 'delegate';
  uiModeOverride?: UIMode;

  // Callbacks
  onFieldChange: (key: string, value: unknown) => void;
  onSendMessage?: (message: string) => void;
  onSubmit: () => void;
  onClearError?: () => void;

  // Loading states
  isFieldUpdating?: boolean;
  isChatSending?: boolean;
  isSubmitting?: boolean;

  // Customization
  showHeader?: boolean;
  showProgress?: boolean;
  showChat?: boolean;
  chatCollapsible?: boolean;
  submitLabel?: string;
  chatPlaceholder?: string;
  headerActions?: React.ReactNode;
  headerChips?: Array<{ label: string; color?: string; bgcolor?: string }>;
}

export const TaskInteractionPanel: React.FC<TaskInteractionPanelProps> = ({
  // Content
  title,
  description,
  requestSpec,

  // State
  status,
  values,
  satisfaction,
  messages = [],
  error,

  // Mode
  mode,
  uiModeOverride,

  // Callbacks
  onFieldChange,
  onSendMessage,
  onSubmit,
  onClearError,

  // Loading
  isFieldUpdating = false,
  isChatSending = false,
  isSubmitting = false,

  // Customization
  showHeader = true,
  showProgress = true,
  showChat = true,
  chatCollapsible = true,
  submitLabel,
  chatPlaceholder,
  headerActions,
  headerChips,
}) => {
  const [chatInput, setChatInput] = useState('');

  // Derive fields from requestSpec or use empty array
  const fields = requestSpec?.allowedFields || [];

  // Determine UI mode
  const uiMode = uiModeOverride || determineUIMode(requestSpec);

  // Calculate progress
  const requiredFields = fields.filter((f) => f.required);
  const missingCount = satisfaction?.missingFields?.length || requiredFields.length;
  const collectedCount = requiredFields.length - missingCount;
  const progressPercent = requiredFields.length > 0
    ? Math.round((collectedCount / requiredFields.length) * 100)
    : 0;

  // Build field status for progress indicator
  const fieldStatuses: FieldStatus[] = fields.map((f) => ({
    key: f.key,
    label: f.label,
    required: f.required,
    completed: !satisfaction?.missingFields?.includes(f.key),
  }));

  // Handle chat send
  const handleChatSend = useCallback(() => {
    if (chatInput.trim() && onSendMessage) {
      onSendMessage(chatInput.trim());
      setChatInput('');
    }
  }, [chatInput, onSendMessage]);

  // Determine submit labels based on mode
  const defaultSubmitLabel = mode === 'owner' ? 'Mark Complete' : 'Confirm & Submit';
  const defaultReadyMessage = mode === 'owner'
    ? 'Ready to mark complete!'
    : 'All required information collected!';
  const defaultReadySubMessage = mode === 'owner'
    ? 'All requirements have been satisfied.'
    : 'Click confirm to submit your response.';

  const isFormMode = uiMode === 'form';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.card,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      {showHeader && (
        <TaskHeader
          title={title}
          description={description || requestSpec?.description}
          status={status}
          showStatus={mode === 'owner'}
          chips={headerChips}
          actions={headerActions}
        />
      )}

      {/* Progress */}
      {showProgress && fields.length > 0 && (
        <TaskProgress
          progressPercent={progressPercent}
          progressSummary={satisfaction?.progressSummary}
          fields={fieldStatuses}
        />
      )}

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 2 }} onClose={onClearError}>
          {error}
        </Alert>
      )}

      {/* Main content - Form or Chat first based on uiMode */}
      {isFormMode ? (
        <>
          {/* Form-first: Show structured form */}
          {fields.length > 0 && (
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <DynamicRequestForm
                allowedFields={fields}
                values={values}
                onChange={onFieldChange}
                errors={{}}
                disabled={isFieldUpdating}
              />
            </Box>
          )}

          {/* Collapsible chat section */}
          {showChat && onSendMessage && (chatCollapsible ? (
            <Accordion
              sx={{ borderTop: 1, borderColor: 'divider', boxShadow: 'none', '&:before': { display: 'none' } }}
              defaultExpanded={messages.length > 0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChatIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Need help? Chat with us
                    {messages.length > 0 && ` (${messages.length} messages)`}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TaskChat
                  messages={messages}
                  inputValue={chatInput}
                  onInputChange={setChatInput}
                  onSend={handleChatSend}
                  isSending={isChatSending}
                  placeholder={chatPlaceholder || 'Ask a question or clarify...'}
                  compact
                  maxHeight={200}
                  showTimestamps={false}
                />
              </AccordionDetails>
            </Accordion>
          ) : (
            <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
              <TaskChat
                messages={messages}
                inputValue={chatInput}
                onInputChange={setChatInput}
                onSend={handleChatSend}
                isSending={isChatSending}
                placeholder={chatPlaceholder}
                maxHeight={200}
              />
            </Box>
          ))}
        </>
      ) : (
        <>
          {/* Chat-first: Show messages prominently */}
          {showChat && onSendMessage && (
            <Box sx={{ flex: 1, minHeight: 300 }}>
              <TaskChat
                messages={messages}
                inputValue={chatInput}
                onInputChange={setChatInput}
                onSend={handleChatSend}
                isSending={isChatSending}
                placeholder={chatPlaceholder || 'Type your message...'}
                maxHeight="100%"
              />
            </Box>
          )}

          {/* Show form fields inline if any exist (even in chat mode) */}
          {fields.length > 0 && (
            <Accordion
              sx={{ borderTop: 1, borderColor: 'divider', boxShadow: 'none', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" color="text.secondary">
                  View collected information
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DynamicRequestForm
                  allowedFields={fields}
                  values={values}
                  onChange={onFieldChange}
                  errors={{}}
                  disabled={isFieldUpdating}
                />
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}

      {/* Submit/Complete actions */}
      <TaskActions
        isReady={satisfaction?.isReady || false}
        isSubmitting={isSubmitting}
        onSubmit={onSubmit}
        submitLabel={submitLabel || defaultSubmitLabel}
        submittingLabel="Processing..."
        readyMessage={defaultReadyMessage}
        readySubMessage={defaultReadySubMessage}
      />
    </Box>
  );
};

export default TaskInteractionPanel;
